import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { HubSupportTicketModal } from "@/components/HubSupportTicketModal";
import {
  hubSupportLocalAttachmentFromDataUrl,
  type HubSupportLocalAttachment,
} from "@/lib/hubSupportTicketAttachments";
import { hubSupportTicketPagePath, hubSupportTicketPageUrlFull } from "@/lib/navigationTrail";

type TicketDraft = {
  source: "menu" | "launcher";
  locked: boolean;
  attachments: HubSupportLocalAttachment[];
  incomingAttachment: HubSupportLocalAttachment | null;
  pagePath: string;
  pageUrlFull: string;
};

type HubReportIssueContextValue = {
  openSupportTicketFromMenu: () => void;
};

const HubReportIssueContext = createContext<HubReportIssueContextValue | null>(null);

export function useHubReportIssue(): HubReportIssueContextValue {
  const value = useContext(HubReportIssueContext);
  if (value == null) {
    throw new Error("useHubReportIssue requires HubReportIssueHost");
  }
  return value;
}

function currentPage(): { pagePath: string; pageUrlFull: string } {
  return {
    pagePath: hubSupportTicketPagePath(window.location.pathname, window.location.search),
    pageUrlFull: hubSupportTicketPageUrlFull(window.location),
  };
}

function pushReportButtonEnabled(enabled: boolean): void {
  window.SgsLauncher?.setReportIssueEnabled?.(enabled);
}

export function HubReportIssueHost({ children }: { children: ReactNode }): JSX.Element {
  const [draft, setDraft] = useState<TicketDraft | null>(null);

  const reportEnabled = draft == null || (draft.source === "menu" && !draft.locked);

  const syncReportButton = useCallback(() => {
    pushReportButtonEnabled(reportEnabled);
  }, [reportEnabled]);

  useEffect(() => {
    syncReportButton();
  }, [syncReportButton]);

  useEffect(() => {
    window.__sgsSyncReportIssueButton = () => {
      pushReportButtonEnabled(reportEnabled);
    };
    window.__sgsOnReportScreenshot = (dataUrl: string) => {
      const attachment = hubSupportLocalAttachmentFromDataUrl(dataUrl, "tablet-screen.jpg");
      if (attachment == null) return;
      pushReportButtonEnabled(false);
      setDraft((current) => {
        if (current != null && (current.source === "launcher" || current.locked)) {
          return current;
        }
        if (current != null && current.source === "menu" && !current.locked) {
          return {
            ...current,
            locked: true,
            incomingAttachment: attachment,
          };
        }
        const page = currentPage();
        return {
          source: "launcher",
          locked: true,
          attachments: [attachment],
          incomingAttachment: null,
          pagePath: page.pagePath,
          pageUrlFull: page.pageUrlFull,
        };
      });
    };
    return () => {
      delete window.__sgsOnReportScreenshot;
      delete window.__sgsSyncReportIssueButton;
    };
  }, [reportEnabled]);

  const openSupportTicketFromMenu = useCallback(() => {
    setDraft((current) => {
      if (current != null) return current;
      const page = currentPage();
      return {
        source: "menu",
        locked: false,
        attachments: [],
        incomingAttachment: null,
        pagePath: page.pagePath,
        pageUrlFull: page.pageUrlFull,
      };
    });
  }, []);

  const value = useMemo(() => ({ openSupportTicketFromMenu }), [openSupportTicketFromMenu]);

  return (
    <HubReportIssueContext.Provider value={value}>
      {children}
      {draft != null ? (
        <HubSupportTicketModal
          initialAttachments={draft.attachments}
          incomingAttachment={draft.incomingAttachment}
          pagePathOverride={draft.pagePath}
          pageUrlFullOverride={draft.pageUrlFull}
          onClose={() => setDraft(null)}
        />
      ) : null}
    </HubReportIssueContext.Provider>
  );
}
